import React from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@heroicons/react/24/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { RichText } from "../../../components/KaTeXComponent/RichText";
import { QuestionStatusBadge } from "../../../components/QuestionList/questionStatus";
import { formatDateTime } from "../../../utils/formatDate";
import { linkButtonClass } from "../../Exams/TestBuilder/ui";

// Pieces the Versions tab shares between its list, a version and a comparison.

// "Aigul Bekova", else the login; null for a version nobody made (a baseline, or a deleted account).
export const authorName = (author) => {
  if (!author) return null;
  return [author.first_name, author.last_name].filter(Boolean).join(" ").trim() || author.username || null;
};

// "Нұсқа 3"
export const versionLabel = (t, number) => t("question_versions.version", { number });

// Why the version was saved: created, imported, edited, a draft submitted, or how it was before versions.
export const reasonLabel = (t, reason) => t(`question_versions.reasons.${reason}`, { defaultValue: reason || "" });

// The version's number as the address has it (?v=3), else null.
export const versionNumber = (value) => (/^[1-9]\d{0,8}$/.test(value || "") ? Number(value) : null);

// "Өзгертілді · Aigul Bekova · 23.10.2026, 14:05"
export function VersionByline({ version, className = "" }) {
  const { t, i18n } = useTranslation();
  const name = authorName(version.author);
  return (
    <p className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 ${className}`}>
      <span
        className="font-medium text-slate-700"
        title={version.reason === "baseline" ? t("question_versions.baseline_hint") : undefined}
      >
        {reasonLabel(t, version.reason)}
      </span>
      <span aria-hidden="true">·</span>
      {name ? (
        <span className="min-w-0 break-words">{name}</span>
      ) : (
        <span>
          <span aria-hidden="true">—</span>
          <span className="sr-only">{t("question_versions.no_author")}</span>
        </span>
      )}
      <span aria-hidden="true">·</span>
      <time dateTime={version.created} className="whitespace-nowrap tabular-nums">
        {formatDateTime(version.created, i18n.language)}
      </time>
    </p>
  );
}

// The status the question had when the version was saved (not necessarily its status now).
export function VersionStatus({ code }) {
  const { t } = useTranslation();
  if (!code) return null;
  return (
    <span className="inline-flex shrink-0" title={t("question_versions.status_hint")}>
      <QuestionStatusBadge status={{ code }} />
      <span className="sr-only">({t("question_versions.status_hint")})</span>
    </span>
  );
}

export function LatestTag() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
      {t("question_versions.latest")}
    </span>
  );
}

const OPTION_TONES = {
  plain: "bg-white ring-slate-200",
  changed: "bg-white ring-amber-300",
  added: "bg-emerald-50 ring-emerald-200",
  removed: "bg-red-50 ring-red-200",
};

/**
 * An answer option as the question page shows it, its letter filled when it is the correct one, plus a "correct"
 * tag (the color alone does not say it). `html` replaces its text (a diff's marks); `tone` frames an option that
 * changed, was added or removed; `children` go at its end (a tag).
 */
export function AnswerOption({ answer, html, tone = "plain", children }) {
  const { t } = useTranslation();
  const correct = !!answer.is_correct;
  const tags = React.Children.toArray(children);
  return (
    <div className={`flex items-center gap-3 rounded-lg px-3 py-2 ring-1 ring-inset ${OPTION_TONES[tone] || OPTION_TONES.plain}`}>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          correct ? "bg-blue-600 text-white shadow-sm shadow-blue-600/25" : "bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200"
        }`}
      >
        {answer.option}
      </span>
      <div className="min-w-0 flex-1 break-words text-slate-700">
        <RichText html={html ?? answer.body} />
      </div>
      {(correct || tags.length > 0) && (
        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
          {correct && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20"
              title={t("question_versions.correct")}
            >
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">{t("question_versions.correct")}</span>
            </span>
          )}
          {tags}
        </div>
      )}
    </div>
  );
}

// A failure inside the tab, with a retry when trying again may help.
export function LoadError({ message, onRetry, className = "" }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className={`flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ${className}`}>
      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0">{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className={linkButtonClass}>
          {t("question_versions.retry")}
        </button>
      )}
    </div>
  );
}

// Grey bars while a version's content loads.
export function ContentSkeleton({ lines = 4 }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      <span className="block h-5 w-2/3 rounded bg-slate-100 motion-safe:animate-pulse" />
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="block h-10 rounded-lg bg-slate-100 motion-safe:animate-pulse" />
      ))}
    </div>
  );
}
